import { useState, useEffect, useMemo } from 'react';
import { resolveMediaUrl } from '@/config/api';
import { supabase } from '@/lib/supabaseClient';

export const TYPE_IMAGES = {
  Sedan: 'https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=400&h=220&fit=crop',
  SUV: 'https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?w=400&h=220&fit=crop',
  MUV: 'https://images.unsplash.com/photo-1549924231-f129b911e442?w=400&h=220&fit=crop',
  Traveller: 'https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?w=400&h=220&fit=crop',
  Luxury: 'https://images.unsplash.com/photo-1555215695-3004980ad54e?w=400&h=220&fit=crop',
};

export const formatVehicleLabel = (v) =>
  `${v.model} - ${v.capacity}+1 Seater (₹${v.rate_per_km}/km)`;

/** Shown when API is unreachable (e.g. phone on LAN before backend hostname fix) */
export const FALLBACK_VEHICLES = [
  { id: 'fallback-1', model: 'Maruti Swift Dzire', capacity: 4, rate_per_km: 11, type: 'Sedan', ac: true },
  { id: 'fallback-2', model: 'Toyota Etios', capacity: 4, rate_per_km: 11, type: 'Sedan', ac: true },
  { id: 'fallback-3', model: 'Maruti Ertiga', capacity: 6, rate_per_km: 14, type: 'MUV', ac: true },
  { id: 'fallback-4', model: 'Kia Carens', capacity: 6, rate_per_km: 18, type: 'SUV', ac: true },
  { id: 'fallback-5', model: 'Toyota Innova Crysta', capacity: 6, rate_per_km: 20, type: 'SUV', ac: true },
  { id: 'fallback-6', model: 'Force Tempo Traveller 12', capacity: 12, rate_per_km: 22, type: 'Traveller', ac: true },
];

/** Map Supabase nested vehicle_images → images (FastAPI shape). */
export function normalizeVehicle(row) {
  if (!row) return row;
  const { vehicle_images, ...rest } = row;
  const images = (vehicle_images || []).slice().sort((a, b) => {
    if (a.category === 'main' && b.category !== 'main') return -1;
    if (b.category === 'main' && a.category !== 'main') return 1;
    return (a.sort_order ?? 0) - (b.sort_order ?? 0);
  });
  return { ...rest, images };
}

export function vehicleCoverImage(v) {
  const images = v.images || [];
  const main = images.find((i) => i.category === 'main');
  if (main?.url) {
    const resolved = resolveMediaUrl(main.url);
    return resolved || main.url;
  }
  if (v.image_url) {
    const resolved = resolveMediaUrl(v.image_url);
    return resolved || v.image_url;
  }
  return TYPE_IMAGES[v.type] || TYPE_IMAGES.Sedan;
}

export function vehicleImage(v) {
  return vehicleCoverImage(v);
}

export function vehicleDisplayImages(v) {
  const images = v.images || [];
  if (images.length) {
    const main = images.find((i) => i.category === 'main');
    const others = images.filter((i) => i.category === 'other');
    const ordered = main ? [main, ...others] : others;
    return ordered.map((img) => resolveMediaUrl(img.url) || img.url);
  }
  return [vehicleCoverImage(v)];
}

export function useVehicles({ activeOnly = false } = {}) {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fromApi, setFromApi] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let query = supabase
          .from('vehicles')
          .select('*, vehicle_images(*)')
          .order('type')
          .order('model');

        if (activeOnly) {
          query = query.eq('status', 'active');
        }

        const { data, error } = await query;
        if (error) throw error;

        if (!cancelled) {
          const list = (data || []).map(normalizeVehicle);
          setVehicles(list.length ? list : FALLBACK_VEHICLES);
          setFromApi(list.length > 0);
        }
      } catch {
        if (!cancelled) {
          setVehicles(FALLBACK_VEHICLES);
          setFromApi(false);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeOnly]);

  const bookingOptions = useMemo(
    () =>
      vehicles.map((v) => ({
        value: formatVehicleLabel(v),
        label: formatVehicleLabel(v),
        id: v.id,
        rate: v.rate_per_km,
      })),
    [vehicles],
  );

  const startingFare = useMemo(() => {
    const sedan = vehicles.find((v) => v.type === 'Sedan');
    return sedan?.rate_per_km ?? vehicles[0]?.rate_per_km ?? 11;
  }, [vehicles]);

  return { vehicles, loading, bookingOptions, startingFare, fromApi };
}
